import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  MagnifyingGlass, CaretLeft, Thermometer, Drop,
  Lightbulb, BatteryFull, MapPin, Warning, Funnel, Package,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DeviceListView({ onSelectDevice, onSelectShipment, onBack }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("device_name");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const fetchDevices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/fleet/devices?limit=5000`);
      const data = res.data;
      setDevices(data.devices || data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  // Reset page when filters/sort change
  useEffect(() => { setPage(0); }, [search, statusFilter, sortBy, sortDir, pageSize]);

  // Filter and sort
  const filtered = devices
    .filter((d) => {
      if (search) {
        const q = search.toLowerCase();
        if (!d.device_name?.toLowerCase().includes(q) &&
            !d.address?.toLowerCase().includes(q) &&
            !d.shipment_name?.toLowerCase().includes(q) &&
            !d.customer?.toLowerCase().includes(q)) return false;
      }
      if (statusFilter === "alert" && d.status !== "alert") return false;
      if (statusFilter === "normal" && d.status === "alert") return false;
      if (statusFilter === "light_breach") {
        const hasLight = d.light && d.light.toLowerCase() !== "dark";
        if (!hasLight) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const aVal = a[sortBy] ?? "";
      const bVal = b[sortBy] ?? "";
      if (sortDir === "asc") return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });

  const alertCount = devices.filter(d => d.status === "alert").length;
  const normalCount = devices.filter(d => d.status !== "alert").length;
  const lightBreachCount = devices.filter(d => d.light && d.light.toLowerCase() !== "dark").length;

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice(page * pageSize, (page + 1) * pageSize);
  const showStart = filtered.length === 0 ? 0 : page * pageSize + 1;
  const showEnd = Math.min(filtered.length, (page + 1) * pageSize);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const SortHeader = ({ col, children }) => (
    <th className="text-left text-zinc-500 font-medium py-2 px-3 cursor-pointer hover:text-zinc-300 select-none"
      onClick={() => handleSort(col)}>
      {children} {sortBy === col && (sortDir === "asc" ? "\u2191" : "\u2193")}
    </th>
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="animate-pulse text-teal-400 font-mono text-sm">Loading devices...</div>
    </div>;
  }

  return (
    <div data-testid="device-list-view" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-zinc-500 hover:text-zinc-300 transition-colors" data-testid="back-to-overview">
            <CaretLeft size={20} />
          </button>
          <h2 className="text-xl font-semibold text-zinc-50">All Devices</h2>
          <Badge variant="outline" className="border-zinc-700 text-xs font-mono">{devices.length}</Badge>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input placeholder="Search by device, shipment, customer or location…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 bg-zinc-900 border-zinc-800 text-sm" data-testid="device-list-search" />
        </div>
        <div className="flex gap-1.5">
          {[
            { key: "all", label: "All", count: devices.length },
            { key: "alert", label: "In Alert", count: alertCount },
            { key: "normal", label: "Normal", count: normalCount },
            { key: "light_breach", label: "Light Breach", count: lightBreachCount },
          ].map((f) => (
            <Button key={f.key} variant={statusFilter === f.key ? "default" : "outline"} size="sm"
              onClick={() => setStatusFilter(f.key)}
              data-testid={`device-filter-${f.key}`}
              className={`text-xs h-8 ${statusFilter === f.key ? "bg-teal-500/20 text-teal-400 border-teal-800" : "border-zinc-700 hover:bg-zinc-800"}`}>
              <Funnel size={12} className="mr-1" />
              {f.label}
              <span className="ml-1.5 text-[10px] opacity-70 font-mono">{f.count}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <ScrollArea className="max-h-[600px] w-full">
          <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[1280px]">
            <thead className="sticky top-0 bg-zinc-900 z-10">
              <tr className="border-b border-zinc-800">
                <SortHeader col="device_name">Device Name</SortHeader>
                <SortHeader col="shipment_name">Shipment</SortHeader>
                <SortHeader col="status">Status</SortHeader>
                <th className="text-left text-zinc-500 font-medium py-2 px-3 whitespace-nowrap">Last Reading</th>
                <SortHeader col="temperature">Temp (\u00b0F)</SortHeader>
                <SortHeader col="humidity">Humidity</SortHeader>
                <th className="text-left text-zinc-500 font-medium py-2 px-3 whitespace-nowrap">Light/Seal</th>
                <SortHeader col="battery_level">Battery</SortHeader>
                <th className="text-left text-zinc-500 font-medium py-2 px-3 whitespace-nowrap">Location</th>
                <SortHeader col="alert_count">Alerts</SortHeader>
                <SortHeader col="event_count">Events</SortHeader>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={11} className="text-center py-12 text-zinc-600">No devices match your filters</td></tr>
              ) : paged.map((d) => (
                <tr key={d.device_name}
                  className="border-b border-zinc-800/50 hover:bg-zinc-800 cursor-pointer transition-colors"
                  onClick={() => onSelectDevice(d.device_name, d.dataset_id)}
                  data-testid={`device-row-${d.device_name}`}>
                  <td className="py-3 px-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${d.status === "alert" ? "bg-amber-400" : "bg-emerald-400"}`} />
                      <span className="font-mono font-semibold text-zinc-100">{d.device_name}</span>
                    </div>
                  </td>
                  {/* Shipment column — clickable when present. Stops row click so
                      we don't navigate to device detail when the operator
                      actually wanted the shipment. */}
                  <td className="py-3 px-3 max-w-[200px]">
                    {d.shipment_id ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); onSelectShipment?.(d.shipment_id); }}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-teal-500/10 border border-teal-500/30 text-teal-300 hover:bg-teal-500/20 hover:border-teal-500/60 transition-colors max-w-full"
                        title={`${d.shipment_name || d.shipment_id} · ${d.customer || ""}`}
                        data-testid={`device-row-shipment-${d.device_name}`}>
                        <Package size={10} weight="duotone" className="flex-shrink-0" />
                        <span className="font-mono text-[11px] truncate">{d.shipment_name || d.shipment_id.slice(0, 8)}</span>
                      </button>
                    ) : (
                      <span className="text-[10px] font-mono text-zinc-600 italic">unassigned</span>
                    )}
                  </td>
                  <td className="py-3 px-3">
                    <Badge className={`text-[10px] ${d.status === "alert" ? "bg-red-500/20 text-red-400" : "bg-emerald-500/20 text-emerald-400"}`}>
                      {d.status}
                    </Badge>
                  </td>
                  <td className="py-3 px-3 font-mono text-zinc-500 text-[11px]">
                    {d.last_reading || "—"}
                  </td>
                  <td className="py-3 px-3 font-mono text-zinc-200">
                    <div className="flex items-center gap-1">
                      <Thermometer size={11} className="text-teal-500" />
                      {d.temperature != null ? `${d.temperature}` : "—"}
                    </div>
                  </td>
                  <td className="py-3 px-3 font-mono text-zinc-200">
                    <div className="flex items-center gap-1">
                      <Drop size={11} className="text-emerald-500" />
                      {d.humidity != null ? `${d.humidity}%` : "—"}
                    </div>
                  </td>
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-1">
                      <Lightbulb size={11} className={d.light?.toLowerCase() !== "dark" ? "text-amber-400" : "text-zinc-600"} />
                      <span className={`font-mono ${d.light?.toLowerCase() !== "dark" && d.light ? "text-amber-400" : "text-zinc-600"}`}>
                        {d.light || "—"}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-3 font-mono text-zinc-200">
                    <div className="flex items-center gap-1">
                      <BatteryFull size={11} className={d.battery_level > 20 ? "text-emerald-400" : "text-red-400"} />
                      {d.battery_level != null ? `${d.battery_level}%` : "—"}
                    </div>
                  </td>
                  <td className="py-3 px-3 text-zinc-500 max-w-[180px] truncate">
                    {d.address ? (
                      <span className="flex items-center gap-1"><MapPin size={10} />{d.address.substring(0, 35)}</span>
                    ) : "—"}
                  </td>
                  <td className="py-3 px-3">
                    {d.alert_count > 0 ? (
                      <Badge className="text-[10px] bg-red-500/20 text-red-400">{d.alert_count}</Badge>
                    ) : <span className="text-zinc-600 font-mono">0</span>}
                  </td>
                  <td className="py-3 px-3 font-mono text-teal-400">{d.event_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </ScrollArea>

        {/* Pagination */}
        <div className="flex items-center justify-between px-3 py-2.5 border-t border-zinc-800 text-xs" data-testid="device-list-pagination">
          <div className="flex items-center gap-2">
            <span className="text-zinc-500 font-mono">{showStart}–{showEnd} of {filtered.length}</span>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
              className="bg-zinc-800 border border-zinc-700 text-zinc-300 text-[11px] font-mono rounded-sm px-1.5 py-0.5"
              data-testid="page-size-select">
              {[25, 50, 100, 250].map((n) => <option key={n} value={n}>{n}/page</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(0)}
              className="text-[10px] h-7 px-2 border-zinc-700 hover:bg-zinc-800 disabled:opacity-30 font-mono"
              data-testid="page-first">«</Button>
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}
              className="text-[10px] h-7 px-2 border-zinc-700 hover:bg-zinc-800 disabled:opacity-30 font-mono"
              data-testid="page-prev">‹</Button>
            <span className="text-zinc-400 font-mono px-2 text-[11px]">
              Page {page + 1} of {totalPages}
            </span>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              className="text-[10px] h-7 px-2 border-zinc-700 hover:bg-zinc-800 disabled:opacity-30 font-mono"
              data-testid="page-next">›</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}
              className="text-[10px] h-7 px-2 border-zinc-700 hover:bg-zinc-800 disabled:opacity-30 font-mono"
              data-testid="page-last">»</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
